import {
  Alert,
  AlertDescription,
} from "@arcadeai/design-system/components/ui/atoms/alert";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { captureException } from "@sentry/react";
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  StripeAddressElementOptions,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { AlertCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { isPlanUpgrade } from "../utils";

// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const paymentElementOptions = {
  layout: {
    type: "tabs",
    defaultCollapsed: false,
  },
  business: {
    name: "Arcade AI, Inc.",
  },
} as StripePaymentElementOptions;

// biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
const addressElementOptions = {
  mode: "billing",
} as StripeAddressElementOptions;

type CheckoutFormProps = {
  paymentIntentSecret: string;
  setupIntentSecret: string;
  planId?: string;
};

export const CheckoutForm = ({
  paymentIntentSecret,
  setupIntentSecret,
  planId,
}: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const clientSecret = paymentIntentSecret || setupIntentSecret; // prefer payment if present to collect up front payment

  const { organizationId, projectId } = useParams({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/billing/upgrade/checkout/$planId",
  });
  const search = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/billing/upgrade/checkout/$planId",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Determine if this is an upgrade or downgrade
  const isUpgrade = isPlanUpgrade(search.originalPlanId || null, planId || "");

  const handleError = (error: string) => {
    setErrorMessage(error);
    setIsLoading(false);
    toast.error("Payment failed", {
      description: error,
      duration: 5000,
    });
  };

  const handleSystemError = (error: string | Error, userMessage?: string) => {
    const displayMessage =
      userMessage || "An unexpected error occurred. Please try again.";
    setErrorMessage(displayMessage);
    setIsLoading(false);

    const sentryError =
      error instanceof Error ? error : new Error(String(error));
    captureException(sentryError);

    toast.error("Payment failed", {
      description: displayMessage,
      duration: 5000,
    });
  };

  const buildProcessingUrl = () => {
    const baseUrl = `${window.location.origin}/orgs/${organizationId}/projects/${projectId}/billing/upgrade/processing/${planId}`;
    return search.originalPlanId
      ? `${baseUrl}?originalPlanId=${encodeURIComponent(search.originalPlanId)}`
      : baseUrl;
  };

  const buildConfirmParams = () => ({
    return_url: planId
      ? buildProcessingUrl()
      : `${window.location.origin}/orgs/${organizationId}/projects/${projectId}/billing/overview`,
  });

  const confirmPayment = async () => {
    const confirmParams = buildConfirmParams();
    const commonOptions = {
      ...(elements ? { elements } : {}),
      clientSecret,
      confirmParams,
      // Uncomment below if you only want redirect for redirect-based payments
      // redirect: 'if_required',
    };

    if (!stripe) {
      throw new Error("Stripe not initialized");
    }

    return paymentIntentSecret
      ? await stripe.confirmPayment(commonOptions)
      : await stripe.confirmSetup(commonOptions);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!(elements && stripe)) {
      handleSystemError(
        new Error("Stripe elements or stripe not initialized"),
        "Payment system not ready. Please try again."
      );
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      // Trigger form validation
      const { error: submitError } = await elements.submit();
      if (submitError?.message) {
        handleError(submitError.message);
        return;
      }

      // Confirm payment using the appropriate method
      const { error } = await confirmPayment();

      if (error) {
        handleError(error.message ?? "An unknown error occurred");
      }
    } catch (err) {
      // For unexpected system errors, always log to Sentry
      handleSystemError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {!!errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <PaymentElement options={paymentElementOptions} />
      </div>

      <div className="space-y-4">
        <AddressElement options={addressElementOptions} />
      </div>

      <div className="flex flex-col-reverse gap-3 pt-6 sm:flex-row">
        <Link
          className="flex-1"
          from="/orgs/$organizationId/projects/$projectId/billing/upgrade"
          to="."
        >
          <Button
            className="w-full"
            disabled={isLoading}
            type="button"
            variant="outline"
          >
            Back to Plans
          </Button>
        </Link>
        <Button
          className="flex-1 bg-[#00FF67] font-medium text-black hover:bg-[#00FF67]/80"
          disabled={isLoading || !stripe || !elements}
          type="submit"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing Payment...
            </>
          ) : (
            `Complete ${isUpgrade ? "Upgrade" : "Downgrade"}`
          )}
        </Button>
      </div>

      {/* Terms and conditions */}
      <div className="pt-4 pb-2 text-center">
        <p className="text-muted-foreground text-xs">
          By completing this purchase, you agree to our{" "}
          <a
            className="text-green-500 underline underline-offset-2 hover:text-green-300"
            href="https://www.arcade.dev/terms-of-service"
            rel="noopener noreferrer"
            target="_blank"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            className="text-green-500 underline underline-offset-2 hover:text-green-300"
            href="https://www.arcade.dev/privacy-policy"
            rel="noopener noreferrer"
            target="_blank"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>

      {!!isLoading && (
        <div className="pt-4 text-center">
          <p className="text-muted-foreground text-sm">
            🔒 Please don't close this page while we securely process your
            payment.
          </p>
        </div>
      )}
    </form>
  );
};
